import { CANVAS_TOOLS, normalizeBox } from "../nativeCanvasElements";
import {
  CLICK_DRAG_THRESHOLD_PX,
  MARQUEE_MIN_SIZE,
} from "../canvasDefaults";
import { ARROW_BINDING_GAP, findBindTarget } from "../geometry/arrowBinding";
import { doBoxesIntersect, getElementBounds } from "../geometry/elementBounds";
import { getElementAtPoint } from "../geometry/elementHitTest";
import { panViewport } from "../geometry/viewportTransform";
import { createDraftActions } from "./createSession";
import {
  applyLiftedElements,
  buildArrowEndpointLifted,
  buildMovedLifted,
  buildResizedLifted,
} from "./liftedElements";
import {
  findHandleSession,
  getHitPadding,
  getHoverFeedback,
  SESSION_TYPES,
} from "./sessionHandles";

const MIDDLE_MOUSE_BUTTON = 1;
const SHAPE_CREATE_TOOLS = Object.freeze([
  CANVAS_TOOLS.ARROW,
  CANVAS_TOOLS.DIAMOND,
  CANVAS_TOOLS.DRAW,
  CANVAS_TOOLS.ELLIPSE,
  CANVAS_TOOLS.RECTANGLE,
]);

const getScreenDistance = (first, second) =>
  Math.hypot(first.x - second.x, first.y - second.y);

const getNextSelectedIds = ({ elementId, selectedIds, shouldToggle }) => {
  if (!shouldToggle) {
    return selectedIds.has(elementId) ? selectedIds : new Set([elementId]);
  }
  const nextIds = new Set(selectedIds);
  if (nextIds.has(elementId)) nextIds.delete(elementId);
  else nextIds.add(elementId);

  return nextIds;
};

/**
 * Gesture state machine for the canvas. Mutates engine state directly;
 * pointer moves never trigger React renders — only overlay/static repaints
 * via the rAF render scheduler.
 * @param {{ internals: object, state: object }} params
 * @returns {object}
 */
export const createPointerHandlers = ({ internals, state }) => {
  const setBindTarget = (elementId) => {
    if (state.bindTargetId === elementId) return;
    state.bindTargetId = elementId;
    internals.requestRender("overlay");
  };
  const draftActions = createDraftActions({ internals, setBindTarget, state });

  const trackDragDistance = (payload) => {
    const session = state.session;
    session.hasDragged =
      session.hasDragged ||
      getScreenDistance(payload.screenPoint, session.originScreen) >=
        CLICK_DRAG_THRESHOLD_PX;
  };

  const startMove = (payload, element) => {
    const nextIds = getNextSelectedIds({
      elementId: element.id,
      selectedIds: state.selectedIds,
      shouldToggle: payload.shiftKey || payload.metaKey,
    });
    internals.setSelectedIds(nextIds);
    if (!nextIds.has(element.id)) return;

    state.session = {
      hasDragged: false,
      origin: payload.point,
      originScreen: payload.screenPoint,
      originals: state.elements.filter((sceneElement) =>
        nextIds.has(sceneElement.id)
      ),
      type: SESSION_TYPES.MOVE,
    };
  };

  const startSelectSession = (payload) => {
    const handleSession = findHandleSession({
      screenPoint: payload.screenPoint,
      state,
    });
    if (handleSession) {
      state.session = { ...handleSession, hasDragged: false };
      return;
    }
    const element = getElementAtPoint({
      elements: state.elements,
      padding: getHitPadding(state),
      point: payload.point,
    });
    if (element) return startMove(payload, element);

    if (!payload.shiftKey) internals.setSelectedIds(new Set());
    state.session = {
      baseIds: payload.shiftKey ? new Set(state.selectedIds) : new Set(),
      origin: payload.point,
      type: SESSION_TYPES.MARQUEE,
    };
  };

  const handlePointerDown = (payload) => {
    state.lastPointerScenePoint = payload.point;
    internals.commitTextEditing();
    const isPanTrigger =
      state.isSpacePanning ||
      state.tool === CANVAS_TOOLS.HAND ||
      payload.button === MIDDLE_MOUSE_BUTTON;
    if (isPanTrigger) {
      state.session = {
        lastScreenPoint: payload.screenPoint,
        type: SESSION_TYPES.PAN,
      };
      return internals.setCursor("grabbing");
    }
    if (state.tool === CANVAS_TOOLS.SELECT) return startSelectSession(payload);
    if (state.tool === CANVAS_TOOLS.TEXT) {
      internals.handleToolUsed();
      return internals.insertTextElementAt(payload.point);
    }
    if (SHAPE_CREATE_TOOLS.includes(state.tool)) draftActions.startCreate(payload);
  };

  const updateMarquee = (payload) => {
    const marqueeBox = normalizeBox({
      end: payload.point,
      start: state.session.origin,
    });
    state.marqueeBox = marqueeBox;
    const isTooSmall =
      marqueeBox.width < MARQUEE_MIN_SIZE && marqueeBox.height < MARQUEE_MIN_SIZE;
    const coveredIds = isTooSmall
      ? []
      : state.elements
          .filter((element) =>
            doBoxesIntersect(getElementBounds(element), marqueeBox)
          )
          .map((element) => element.id);
    internals.setSelectedIds(new Set([...state.session.baseIds, ...coveredIds]));
    internals.requestRender("overlay");
  };

  const buildSessionLifted = (payload) => {
    const session = state.session;
    if (session.type === SESSION_TYPES.MOVE) {
      trackDragDistance(payload);
      return buildMovedLifted({
        delta: {
          x: payload.point.x - session.origin.x,
          y: payload.point.y - session.origin.y,
        },
        elements: state.elements,
        originals: session.originals,
      });
    }
    session.hasDragged = true;
    if (session.type === SESSION_TYPES.RESIZE) {
      return buildResizedLifted({
        elements: state.elements,
        handle: session.handle,
        original: session.original,
        point: payload.point,
      });
    }
    const bindTarget = findBindTarget({
      elements: state.elements,
      point: payload.point,
    });
    setBindTarget(bindTarget?.id ?? null);

    return buildArrowEndpointLifted({
      bindGap: ARROW_BINDING_GAP,
      bindTarget,
      elements: state.elements,
      endpoint: session.endpoint,
      original: session.original,
      point: payload.point,
    });
  };

  const updatePanSession = (payload) => {
    internals.updateViewport(
      panViewport({
        delta: {
          x: payload.screenPoint.x - state.session.lastScreenPoint.x,
          y: payload.screenPoint.y - state.session.lastScreenPoint.y,
        },
        viewport: state.viewport,
      })
    );
    state.session.lastScreenPoint = payload.screenPoint;
  };

  const handlePointerMove = (payload) => {
    state.lastPointerScenePoint = payload.point;
    if (!state.session) {
      const feedback = getHoverFeedback({ payload, state });
      setBindTarget(feedback.bindTargetId);
      if (feedback.cursor) internals.setCursor(feedback.cursor);
      return;
    }
    const sessionType = state.session.type;
    if (sessionType === SESSION_TYPES.PAN) return updatePanSession(payload);
    if (sessionType === SESSION_TYPES.MARQUEE) return updateMarquee(payload);
    if (sessionType === SESSION_TYPES.CREATE) {
      trackDragDistance(payload);
      return draftActions.updateDraft(payload);
    }
    const isFirstLift = !state.liftedElements;
    state.liftedElements = buildSessionLifted(payload);
    internals.requestRender(isFirstLift ? "both" : "overlay");
  };

  const finishLiftedGesture = (session) => {
    const lifted = state.liftedElements;
    state.liftedElements = null;
    setBindTarget(null);
    if (!lifted || !session.hasDragged) return internals.requestRender("both");

    internals.commitElements(
      applyLiftedElements({ elements: state.elements, lifted })
    );
  };

  const handlePointerUp = () => {
    const session = state.session;
    if (!session) return;
    state.session = null;

    if (session.type === SESSION_TYPES.PAN) {
      return internals.setCursor(internals.getBaseCursor());
    }
    if (session.type === SESSION_TYPES.MARQUEE) {
      state.marqueeBox = null;
      return internals.requestRender("overlay");
    }
    if (session.type === SESSION_TYPES.CREATE) {
      return draftActions.finishCreate(session);
    }
    finishLiftedGesture(session);
  };

  const handleDoubleClick = (payload) => {
    const element = getElementAtPoint({
      elements: state.elements,
      padding: getHitPadding(state),
      point: payload.point,
    });
    if (element) return internals.startTextEditing(element.id);
    internals.insertTextElementAt(payload.point);
  };

  const cancelSession = () => {
    state.session = null;
    state.draftElement = null;
    state.liftedElements = null;
    state.marqueeBox = null;
    state.bindTargetId = null;
    internals.requestRender("both");
  };

  return {
    cancelSession,
    handleDoubleClick,
    handlePointerDown,
    handlePointerMove,
    handlePointerUp,
    hasActiveSession: () => Boolean(state.session),
  };
};
